import React, { createContext, useContext, useEffect, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, type Profile, type ArtistProfile } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createErrorTracker } from '../lib/error-tracker';

const errorTracker = createErrorTracker('AuthContext');

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  artistProfile: ArtistProfile | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, username: string) => Promise<void>;
  signOut: () => Promise<void>;
  requestArtistAccess: (data: {
    genre: string[];
    phoneNumber: string;
    email: string;
    agreementAccepted: boolean;
    ipAddress: string;
  }) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [artistProfile, setArtistProfile] = useState<ArtistProfile | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes - DO NOT use async callback directly to avoid deadlocks
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        // Wrap async operations in an IIFE to avoid deadlock
        (async () => {
          await fetchProfile(session.user.id);
        })();
      } else {
        setProfile(null);
        setArtistProfile(null);
        setIsAdmin(false);
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  const fetchProfile = async (userId: string) => {
    try {
      // Fetch user profile
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (profileError) {
        if (profileError.code === 'PGRST116') {
          // Profile doesn't exist yet, which is fine for new users
          setProfile(null);
        } else {
          throw profileError;
        }
      } else {
        setProfile(profileData);

        // If user is an artist, fetch artist profile
        if (profileData?.is_artist) {
          const { data: artistData, error: artistError } = await supabase
            .from('artist_profiles')
            .select('*')
            .eq('id', userId)
            .single();

          if (artistError && artistError.code !== 'PGRST116') throw artistError;
          setArtistProfile(artistData);
        }
      }

      const hasAdminFromProfile = profileData?.is_admin === true ||
        profileData?.role === 'admin' ||
        (Array.isArray(profileData?.roles) && profileData.roles.includes('admin'));

      let hasAdminFromRoleTables = false;
      try {
        const [rolesRes, adminRolesRes] = await Promise.all([
          supabase.from('user_roles').select('role_id').eq('user_id', userId),
          supabase.from('admin_user_roles').select('role_id').eq('user_id', userId),
        ]);
        hasAdminFromRoleTables =
          rolesRes.data?.some(role => role.role_id === 'admin') ||
          adminRolesRes.data?.some(role => ['super_admin', 'admin', 'editor', 'support'].includes(role.role_id)) ||
          false;
      } catch {
        hasAdminFromRoleTables = false;
      }

      setIsAdmin(hasAdminFromProfile || hasAdminFromRoleTables);
    } catch (error) {
      errorTracker.trackDatabaseError(error, 'fetchProfile', {
        userId,
        toastMessage: 'We had trouble loading your profile. Please refresh the page to try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      console.error('Error signing in:', error);
      throw error;
    }

    if (!data.user) {
      throw new Error('No user returned after sign in');
    }

    toast.success('Welcome back!');
    navigate('/');
  };

  const signUp = async (email: string, password: string, username: string) => {
    // Check if username is taken
    const { data: existingUser } = await supabase
      .from('user_profiles')
      .select('username')
      .eq('username', username)
      .maybeSingle();

    if (existingUser) {
      throw new Error('Username is already taken');
    }

    const { data: { user }, error: signUpError } = await supabase.auth.signUp({
      email,
      password,
    });

    if (signUpError) throw signUpError;
    if (!user) throw new Error('No user returned after signup');

    // Create user profile
    const { error: profileError } = await supabase
      .from('user_profiles')
      .insert([{ id: user.id, username }]);

    if (profileError) throw profileError;

    toast.success('Account created successfully!');
    navigate('/');
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      errorTracker.trackAuthError(error, 'signOut', {
        toastMessage: 'We had trouble signing you out. Please try again.'
      });
      throw error;
    }
    toast.success('Signed out successfully');
    navigate('/login');
  };

  const requestArtistAccess = async (data: {
    genre: string[];
    phoneNumber: string;
    email: string;
    agreementAccepted: boolean;
    ipAddress: string;
    artistName?: string;
  }) => {
    if (!user) {
      toast.error('You must be logged in to request artist access');
      return;
    }

    if (!data.agreementAccepted) {
      toast.error('You must accept the revenue split agreement');
      return;
    }

    try {
      setLoading(true);

      let currentProfile = profile;
      if (!currentProfile) {
        const { data: newProfile, error: createProfileError } = await supabase
          .from('user_profiles')
          .insert([{
            id: user.id,
            email: data.email,
            username: data.email.split('@')[0],
            is_artist: true,
            phone_number: data.phoneNumber
          }])
          .select()
          .single();

        if (createProfileError) throw createProfileError;
        currentProfile = newProfile;
        setProfile(newProfile);
      } else {
        const { error: profileUpdateError } = await supabase
          .from('user_profiles')
          .update({
            is_artist: true,
            email: data.email,
            phone_number: data.phoneNumber
          })
          .eq('id', user.id);

        if (profileUpdateError) throw profileUpdateError;
      }

      const { error: artistError } = await supabase
        .from('artist_profiles')
        .insert([{
          id: user.id,
          genre: data.genre,
          approval_status: 'pending',
          phone_number: data.phoneNumber,
          agreement_accepted: data.agreementAccepted,
          agreement_accepted_at: new Date().toISOString(),
          agreement_ip_address: data.ipAddress,
          agreement_version: '1.0'
        }]);

      if (artistError) throw artistError;

      const artistName = data.artistName || currentProfile?.display_name || currentProfile?.username || 'Unknown Artist';
      const { error: permissionError } = await supabase
        .from('artist_permissions')
        .insert([{
          user_id: user.id,
          artist_name: artistName,
          email: data.email,
          can_auto_upload: false,
          application_status: 'pending',
          applied_at: new Date().toISOString()
        }]);

      if (permissionError) throw permissionError;

      toast.success('Artist registration completed! Your application is pending review.');
      await fetchProfile(user.id);
      navigate('/artist/pending');
    } catch (error) {
      errorTracker.trackDatabaseError(error, 'requestArtistAccess', {
        metadata: { email: data.email, genre: data.genre },
        toastMessage: 'Unable to complete your artist registration. Please try again or contact support.'
      });
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    profile,
    artistProfile,
    isAdmin,
    loading,
    signIn,
    signUp,
    signOut,
    requestArtistAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};